import React, { useState } from 'react';
import { CornerDownRight, Loader2, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * One comment plus the business owner's response.
 *
 * Declared at module scope, not inside the list: a component defined inside a
 * render gets a new identity every keystroke, React remounts the reply textarea
 * and focus is lost after each character.
 */
const CommentRow = ({ comment, slug, canReply, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(comment.reply?.text || '');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const body = text.trim();
    if (body.length < 2) return;
    setBusy(true);
    try {
      await api.post(`/directory/${slug}/comments/${comment.id}/reply`, { text: body });
      toast.success('Your response is live.');
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not post your response.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/directory/${slug}/comments/${comment.id}/reply`);
      toast.success('Response removed.');
      setText('');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove the response.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-b border-gray-100 last:border-0 pb-4 last:pb-0" data-testid="directory-comment">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-sm font-bold text-gray-900">{comment.user_name}</span>
        {comment.created_at && (
          <span className="text-xs font-semibold text-gray-500">· {fmtDate(comment.created_at)}</span>
        )}
      </div>
      <p className="text-sm text-gray-700 mt-1.5 whitespace-pre-wrap">{comment.text}</p>

      {comment.reply && (
        <div
          className="mt-3 ml-4 pl-3 border-l-2 rounded-r-lg bg-gray-50/70 py-2 pr-3"
          style={{ borderColor: BRAND_BLUE }}
          data-testid="directory-comment-reply"
        >
          <div className="flex flex-wrap items-baseline gap-x-2">
            <CornerDownRight className="w-3.5 h-3.5" style={{ color: BRAND_BLUE }} />
            <span className="text-sm font-bold" style={{ color: BRAND_BLUE }}>
              {comment.reply.author_name}
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Business response</span>
            {comment.reply.created_at && (
              <span className="text-xs font-semibold text-gray-500">· {fmtDate(comment.reply.created_at)}</span>
            )}
          </div>
          <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{comment.reply.text}</p>
        </div>
      )}

      {canReply && !open && (
        <div className="mt-2 flex items-center gap-3">
          <button
            type="button"
            onClick={() => { setText(comment.reply?.text || ''); setOpen(true); }}
            data-testid="directory-comment-reply-open"
            className="text-xs font-medium hover:underline"
            style={{ color: BRAND_BLUE }}
          >
            {comment.reply ? 'Edit response' : 'Respond'}
          </button>
          {comment.reply && (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="text-xs font-medium text-gray-400 hover:text-red-600 flex items-center gap-1 disabled:opacity-50"
            >
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          )}
        </div>
      )}

      {canReply && open && (
        <div className="mt-2 ml-4 flex items-start gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            maxLength={1000}
            placeholder="Respond as the business…"
            data-testid="directory-comment-reply-input"
            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={submit}
              disabled={busy || text.trim().length < 2}
              data-testid="directory-comment-reply-submit"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: BRAND_BLUE }}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Post
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const DirectoryCommentList = ({ comments = [], slug, canReply = false, onChanged }) => (
  <div className="space-y-4">
    {comments.map((c) => (
      <CommentRow key={c.id} comment={c} slug={slug} canReply={canReply} onChanged={onChanged} />
    ))}
  </div>
);

export default DirectoryCommentList;
